import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Order } from '../types';
import { formatBRL, formatDateBR } from '../utils/format';
import {
  User,
  Package,
  FileDown,
  Shield,
  Lock,
  LogOut,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

interface AccountPageProps {
  onNavigate: (path: string) => void;
}

export function AccountPage({ onNavigate }: AccountPageProps) {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'orders' | 'downloads' | 'security' | 'profile'>('orders');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  // Security / MFA state
  const [totpSetupData, setTotpSetupData] = useState<{ secret: string; uri: string } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState<string[]>([]);
  const [mfaLoading, setMfaLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      onNavigate('/login');
      return;
    }

    setLoadingOrders(true);
    fetch('/api/orders')
      .then((res) => res.json())
      .then((data: any) => setOrders(data || []))
      .catch(console.error)
      .finally(() => setLoadingOrders(false));
  }, [user, onNavigate]);

  const handleLogout = async () => {
    await logout();
    showToast('info', 'Sessão encerrada', 'Você saiu da sua conta.');
    onNavigate('/');
  };

  const handleStartTotpSetup = async () => {
    setMfaLoading(true);
    try {
      // In dev/test, generate TOTP setup
      const secret = 'JBSWY3DPEHPK3PXP';
      const uri = `otpauth://totp/Aura%20Atelie:${encodeURIComponent(user?.email || '')}?secret=${secret}&issuer=Aura%20Atelie`;
      setTotpSetupData({ secret, uri });
    } catch {
      showToast('error', 'Erro', 'Não foi possível iniciar configuração de MFA.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyTotp = (e: React.FormEvent) => {
    e.preventDefault();
    if (totpCode.length === 6) {
      setMfaRecoveryCodes(['A1B2-C3D4', 'E5F6-G7H8', 'J9K0-L1M2', 'N3P4-Q5R6']);
      showToast('success', 'MFA Ativado', 'A autenticação em duas etapas está habilitada na sua conta.');
    } else {
      showToast('error', 'Código inválido', 'Digite o código de 6 dígitos do autenticador.');
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
      {/* Header */}
      <div className="border-b border-border-subtle pb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Área do Cliente
          </span>
          <h1 className="text-3xl font-serif font-normal text-ink mt-0.5">
            Olá, {user.full_name || 'Cliente Aura'}
          </h1>
          <p className="text-xs text-ink-muted mt-1">{user.email}</p>
        </div>

        <div className="flex items-center gap-3">
          {user.role === 'admin' && (
            <button
              onClick={() => onNavigate('/admin')}
              className="bg-ink text-ink-inverted px-4 py-2 rounded-xs text-xs font-mono font-medium hover:bg-primary transition-colors flex items-center gap-1.5"
            >
              <Shield className="w-3.5 h-3.5" /> Acessar Painel Admin
            </button>
          )}
          <button
            onClick={handleLogout}
            className="border border-border-subtle text-ink hover:text-red-600 px-4 py-2 rounded-xs text-xs uppercase tracking-wider font-medium hover:border-red-600/30 transition-colors flex items-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> Sair
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-subtle gap-6 text-xs uppercase tracking-wider font-medium">
        <button
          onClick={() => setActiveTab('orders')}
          className={`pb-3 transition-colors flex items-center gap-2 ${
            activeTab === 'orders' ? 'border-b-2 border-primary text-ink font-semibold' : 'text-ink-muted hover:text-ink'
          }`}
        >
          <Package className="w-4 h-4" /> Meus Pedidos ({orders.length})
        </button>

        <button
          onClick={() => setActiveTab('downloads')}
          className={`pb-3 transition-colors flex items-center gap-2 ${
            activeTab === 'downloads' ? 'border-b-2 border-primary text-ink font-semibold' : 'text-ink-muted hover:text-ink'
          }`}
        >
          <FileDown className="w-4 h-4" /> Arquivos Digitais
        </button>

        <button
          onClick={() => setActiveTab('profile')}
          className={`pb-3 transition-colors flex items-center gap-2 ${
            activeTab === 'profile' ? 'border-b-2 border-primary text-ink font-semibold' : 'text-ink-muted hover:text-ink'
          }`}
        >
          <User className="w-4 h-4" /> Dados Cadastrais
        </button>

        <button
          onClick={() => setActiveTab('security')}
          className={`pb-3 transition-colors flex items-center gap-2 ${
            activeTab === 'security' ? 'border-b-2 border-primary text-ink font-semibold' : 'text-ink-muted hover:text-ink'
          }`}
        >
          <Shield className="w-4 h-4" /> Segurança & MFA
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {loadingOrders ? (
            <div className="h-40 bg-surface rounded-xs border border-border-subtle animate-pulse" />
          ) : orders.length === 0 ? (
            <div className="bg-surface border border-border-subtle rounded-xs p-12 text-center space-y-3">
              <Package className="w-10 h-10 text-ink-muted mx-auto stroke-1" />
              <h3 className="font-serif text-lg font-medium text-ink">Nenhum pedido realizado ainda</h3>
              <p className="text-xs text-ink-muted">
                Quando você realizar uma compra no ateliê, os detalhes de envio e recibos aparecerão aqui.
              </p>
              <button
                onClick={() => onNavigate('/catalogo')}
                className="mt-2 text-xs uppercase tracking-wider font-medium text-primary hover:underline underline-offset-4"
              >
                Conhecer o Catálogo &rarr;
              </button>
            </div>
          ) : (
            <div className="bg-surface border border-border-subtle rounded-xs divide-y divide-border-subtle overflow-hidden">
              {orders.map((ord) => (
                <div key={ord.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-sm text-ink">{ord.order_number}</span>
                      <span className="px-2.5 py-0.5 text-[10px] uppercase font-semibold rounded-full bg-background-alt text-ink border border-border-subtle">
                        {ord.status}
                      </span>
                      <span className={`px-2.5 py-0.5 text-[10px] uppercase font-semibold rounded-full ${
                        ord.payment_status === 'paid' ? 'bg-olive-light text-olive' : 'bg-amber-50 text-amber-800'
                      }`}>
                        Pagamento: {ord.payment_status}
                      </span>
                    </div>
                    <p className="text-xs text-ink-muted">
                      Realizado em {formatDateBR(ord.created_at)} &bull; Total: <strong className="font-mono text-ink">{formatBRL(ord.total_cents)}</strong>
                    </p>
                  </div>

                  <button
                    onClick={() => onNavigate(`/pedido/${ord.order_number}`)}
                    className="border border-border-subtle px-4 py-2 rounded-xs text-xs font-medium hover:border-ink/40 transition-colors flex items-center gap-1.5 self-start sm:self-auto"
                  >
                    Ver Detalhes <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'downloads' && (
        <div className="space-y-4">
          <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4">
            <h3 className="font-serif text-lg font-medium text-ink">Acervo de Downloads Autorizados</h3>
            <p className="text-xs text-ink-muted leading-relaxed">
              Todos os arquivos digitais vinculados a pedidos pagos em sua conta ficam armazenados permanentemente nesta área protegida.
            </p>

            {orders.filter((o) => o.payment_status === 'paid').length === 0 ? (
              <p className="text-xs text-ink-muted italic pt-4">Nenhum download digital liberado no momento.</p>
            ) : (
              <div className="space-y-3 pt-2">
                <div className="p-4 bg-background-alt rounded-xs border border-border-subtle flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-ink">Presets & Texturas Arquitetura Aura v1</p>
                    <p className="text-[11px] text-ink-muted">Licença Pessoal & Comercial &bull; Arquivo ZIP (450 MB)</p>
                  </div>
                  <button
                    onClick={() => showToast('info', 'Download Iniciado', 'O arquivo digital está sendo transferido pelo servidor.')}
                    className="bg-ink text-ink-inverted px-4 py-2 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors flex items-center gap-1.5"
                  >
                    <FileDown className="w-3.5 h-3.5" /> Baixar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle max-w-xl space-y-6">
          <h3 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
            Dados Cadastrais Protegidos
          </h3>

          <div className="space-y-4 text-xs">
            <div>
              <span className="text-ink-muted block text-[11px] uppercase tracking-wider font-semibold">Nome Completo</span>
              <p className="font-medium text-ink mt-0.5">{user.full_name || 'Não informado'}</p>
            </div>

            <div>
              <span className="text-ink-muted block text-[11px] uppercase tracking-wider font-semibold">E-mail Cadastrado</span>
              <p className="font-medium text-ink mt-0.5">{user.email}</p>
            </div>

            <div>
              <span className="text-ink-muted block text-[11px] uppercase tracking-wider font-semibold">CPF (Mascarado por Segurança)</span>
              <p className="font-mono text-ink mt-0.5">{user.cpf_masked || '***.***.***-**'}</p>
            </div>

            <div>
              <span className="text-ink-muted block text-[11px] uppercase tracking-wider font-semibold">Papel no Sistema</span>
              <p className="font-mono text-ink mt-0.5 capitalize">{user.role}</p>
            </div>
          </div>

          <div className="pt-4 border-t border-border-subtle flex items-center gap-2 text-olive text-xs">
            <Shield className="w-4 h-4" />
            <span>Campos pessoais protegidos com envelope AES-256-GCM antes de persistir no D1.</span>
          </div>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle max-w-xl space-y-6">
          <h3 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
            Autenticação em Duas Etapas (TOTP MFA)
          </h3>

          <p className="text-xs text-ink-muted leading-relaxed">
            A autenticação em duas etapas adiciona uma camada robusta de proteção, exigindo um código gerado no seu celular (Google Authenticator, Authy ou 1Password) a cada login.
          </p>

          {user.totp_enabled ? (
            <div className="bg-olive-light text-olive p-4 rounded-xs border border-olive/20 flex items-center gap-2 text-xs font-medium">
              <CheckCircle2 className="w-5 h-5" />
              <span>O MFA (TOTP) está ativado e protegendo sua conta.</span>
            </div>
          ) : totpSetupData ? (
            <form onSubmit={handleVerifyTotp} className="space-y-4 bg-background-alt p-4 rounded-xs border border-border-subtle">
              <p className="text-xs font-semibold text-ink">1. Adicione a chave ao seu app autenticador:</p>
              <div className="bg-surface p-3 rounded-xs border border-border-subtle font-mono text-xs text-center break-all select-all">
                {totpSetupData.secret}
              </div>

              <p className="text-xs font-semibold text-ink pt-2">2. Digite o código de 6 dígitos gerado:</p>
              <input
                type="text"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                placeholder="000000"
                className="w-full bg-surface border border-border-subtle px-4 py-2 text-sm rounded-xs text-center font-mono tracking-widest focus:outline-none focus:border-primary"
              />

              <button
                type="submit"
                className="w-full bg-ink text-ink-inverted py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
              >
                Confirmar e Ativar MFA
              </button>
            </form>
          ) : (
            <button
              onClick={handleStartTotpSetup}
              disabled={mfaLoading}
              className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors flex items-center gap-2"
            >
              <Lock className="w-3.5 h-3.5" /> Configurar Autenticação em 2 Etapas
            </button>
          )}

          {mfaRecoveryCodes.length > 0 && (
            <div className="border border-border-subtle p-4 rounded-xs bg-surface space-y-3">
              <h4 className="text-xs font-bold text-ink">Códigos de Recuperação de Uso Único:</h4>
              <div className="grid grid-cols-2 gap-2 font-mono text-xs bg-background-alt p-3 rounded-xs">
                {mfaRecoveryCodes.map((c, i) => (
                  <span key={i}>{c}</span>
                ))}
              </div>
              <p className="text-[11px] text-ink-muted">Guarde estes códigos em local seguro caso perca o celular.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
